import { Box, Typography } from '@mui/material';
import * as S from './styles';
import type { ActivityTimelineProps } from './types';

/**
 * The flat activity list: a dashed rail of dots, each row an actor sentence with
 * optional status pills, description, row action and timestamp.
 *
 * It renders exactly what it is handed. Ordering, day grouping (as `marker`
 * rows), timestamp formatting and event-type wording all belong to the caller's
 * mapper — every feed has a different event vocabulary, and the ones that merge
 * planes need composite keys. Keeping that out here is what lets the request,
 * request-block and data-analysis feeds share one component.
 */
export function ActivityTimeline({
  entries,
  emptyLabel,
  'data-testid': dataTestId,
}: ActivityTimelineProps) {
  if (entries.length === 0) {
    return (
      <S.TimelineEmptyState variant='caption' data-testid={dataTestId}>
        {emptyLabel}
      </S.TimelineEmptyState>
    );
  }

  const n = entries.length;

  return (
    <S.TimelineList role='list' data-testid={dataTestId}>
      {entries.map((entry, index) => {
        const isFirst = index === 0;
        const isLast = index === n - 1;
        const IconComp = entry.iconComponent;
        const iconNode =
          entry.icon ??
          (IconComp ? (
            <IconComp size={12} strokeWidth={2} />
          ) : (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          ));

        return (
          <S.TimelineItemRoot
            key={entry.id}
            highlighted={entry.highlighted}
            variant={entry.variant ?? 'event'}
            role='listitem'
          >
            <S.TimelineTrack>
              <S.TimelineConnector connectorPosition='top' visible={!isFirst} />
              <S.TimelineDot variant={entry.variant ?? 'event'}>{iconNode}</S.TimelineDot>
              <S.TimelineConnector connectorPosition='bottom' visible={!isLast} />
            </S.TimelineTrack>
            <S.TimelineMain>
              <S.TimelineTitleRow>
                {typeof entry.title === 'string' ? (
                  <Typography
                    variant='caption'
                    fontWeight={entry.variant === 'event' ? 500 : 700}
                    component='span'
                  >
                    {entry.title}
                  </Typography>
                ) : (
                  entry.title
                )}
                {entry.statusBadges?.map((b) => (
                  <S.StatusChip
                    key={b.label}
                    label={b.label}
                    color={b.color}
                    size='small'
                    variant='action'
                  />
                ))}
              </S.TimelineTitleRow>
              {entry.description != null && entry.description !== '' && (
                <S.TimelineDescription variant='caption'>{entry.description}</S.TimelineDescription>
              )}
              {entry.actionLabel && entry.onAction ? (
                <S.TimelineActionButton type='button' onClick={entry.onAction}>
                  {entry.actionLabel}
                </S.TimelineActionButton>
              ) : null}
              {(entry.timestamp || entry.time) && (
                <S.TimelineTimestamp display='block' mt={0.5} variant='caption'>
                  {entry.timestamp && entry.time
                    ? `${entry.timestamp} · ${entry.time}`
                    : (entry.timestamp ?? entry.time)}
                </S.TimelineTimestamp>
              )}
            </S.TimelineMain>
          </S.TimelineItemRoot>
        );
      })}
    </S.TimelineList>
  );
}
